import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  doc, getDoc, collection, addDoc, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import {
  uploadImage, inspectImage, friendlyMessage, logStorageFailure,
} from '../firebase/uploadImage';
import PhoneInput from '../components/PhoneInput';

const STEP_LOOKUP  = 'lookup';
const STEP_PAY     = 'pay';
const STEP_SUCCESS = 'success';

const TIER_LABEL = {
  basic:    'Basic',
  standard: 'Standard',
  premium:  'Premium',
};

export default function UpgradePage() {
  const [cfg,        setCfg]        = useState(null);
  const [cfgLoading, setCfgLoading] = useState(true);
  const [step,       setStep]       = useState(STEP_LOOKUP);
  const [phone,      setPhone]      = useState('+91');
  const [member,     setMember]     = useState(null);
  const [appId,      setAppId]      = useState(null);
  const [error,      setError]      = useState(null);
  const [busy,       setBusy]       = useState(false);

  const [shotFile,    setShotFile]    = useState(null);
  const [shotPreview, setShotPreview] = useState('');
  const shotInputRef = useRef(null);
  const [uploading,   setUploading]   = useState(false);
  const [shotProgress, setShotProgress] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'app_config', 'membership'));
        if (snap.exists()) setCfg(snap.data());
      } catch { /* ignore */ }
      setCfgLoading(false);
    })();
  }, []);

  // Upgrade price = premium - current tier price.
  // For Basic->Premium they pay full Premium (since they didn't pay
  // the logo charge yet). For Standard->Premium they pay just the
  // app-access portion (premium - standard).
  const upgradePrice = useCallback((currentTier) => {
    if (!cfg) return 0;
    const premium  = Number(cfg.tier_premium_price)  || 0;
    const standard = Number(cfg.tier_standard_price) || 0;
    const basic    = Number(cfg.tier_basic_price)    || 0;
    if (currentTier === 'standard') return Math.max(0, premium - standard);
    if (currentTier === 'basic')    return Math.max(0, premium - basic);
    return 0;
  }, [cfg]);

  const handleLookup = useCallback(async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (!/^\+91\d{10}$/.test(phone.trim())) {
      setError('Enter a valid 10-digit mobile number.');
      return;
    }
    setBusy(true);
    try {
      const ref = doc(db, 'allowed_users', phone.trim());
      const snap = await ref.get?.() ?? await getDoc(ref);
      const real = snap.exists?.() ? snap : await getDoc(ref);
      if (!real.exists()) {
        setError("No account found for this number. " +
                 "If you're new, sign up via the Join page.");
      } else {
        const data = real.data() || {};
        if (data.tier === 'premium' && data.mobile_app_access) {
          setError('You already have Premium access — no upgrade needed.');
          return;
        }
        setMember(data);
      }
    } catch (err) {
      setError(`Lookup failed: ${err.message}`);
    } finally {
      setBusy(false);
    }
  }, [phone]);

  const proceedToPayment = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const price = upgradePrice(member?.tier || 'basic');
      const appRef = await addDoc(collection(db, 'membership_applications'), {
        type:           'upgrade',
        upgrading_from: member?.tier || 'basic',
        upgrading_to:   'premium',
        phone_number:   phone.trim(),
        name:           member?.name        || '',
        email:          member?.email       || '',
        district:       member?.district    || '',
        car_variant:    member?.car_variant || '',
        car_color:      member?.car_color   || '',
        vehicle_number: member?.vehicle_number || '',
        member_id:      member?.member_id   || '',
        tier:           'premium',
        amount_inr:     price,
        grants_app_access: true,
        status:         price > 0 ? 'awaiting_payment' : 'awaiting_verification',
        submitted_at:   serverTimestamp(),
      });
      setAppId(appRef.id);
      setStep(STEP_PAY);
    } catch (err) {
      setError(`Failed: ${err.message}`);
    } finally {
      setBusy(false);
    }
  }, [phone, member, upgradePrice]);

  const handleShotChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const check = await inspectImage(file);
    if (!check.ok) { setError(check.reason); return; }
    setError(null);
    setShotFile(file);
    setShotPreview(URL.createObjectURL(file));
  }, []);

  const submitScreenshot = useCallback(async () => {
    if (!shotFile || !appId) return;
    setUploading(true);
    setShotProgress(0);
    setError(null);
    try {
      // Records the Storage path, not a download URL: minting a URL is a
      // Storage `read`, which this unauthenticated page cannot perform.
      const { path } = await uploadImage(shotFile, {
        pathPrefix: 'payment_screenshots',
        id: appId,
        onProgress: setShotProgress,
      });
      await updateDoc(doc(db, 'membership_applications', appId), {
        status:                  'awaiting_verification',
        payment_screenshot_path: path,
        screenshot_uploaded_at:  serverTimestamp(),
      });
      setStep(STEP_SUCCESS);
    } catch (err) {
      logStorageFailure('upgrade_screenshot', err, { docId: appId });
      setError(err.friendly || friendlyMessage(err));
    } finally {
      setUploading(false);
    }
  }, [shotFile, appId]);

  const wrap = (children) => (
    <div style={{
      minHeight: '100vh', background: '#09090B',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px 16px',
    }}>
      <div style={{
        maxWidth: 480, width: '100%',
        background: '#111113',
        border: '1px solid rgba(212, 181, 116, 0.2)',
        borderRadius: 16,
        padding: 28,
        boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 80px rgba(212,181,116,0.08)',
      }}>{children}</div>
    </div>
  );

  if (cfgLoading) return wrap(
    <div style={{ textAlign: 'center', color: '#D8C8A4' }}>Loading…</div>
  );

  if (step === STEP_SUCCESS) return wrap(
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 56, marginBottom: 12 }}>⭐</div>
      <h2 style={{ color: '#F7E8BF', fontSize: 22, fontWeight: 900, marginBottom: 10 }}>
        Upgrade request received
      </h2>
      <p style={{ color: '#D8C8A4', fontSize: 14, lineHeight: 1.6 }}>
        We'll verify your payment within 24 hours. Once approved your
        tier will flip to Premium and you can log in to the app.
      </p>
    </div>
  );

  if (step === STEP_PAY) {
    const amount    = upgradePrice(member?.tier || 'basic');
    const upiId     = cfg?.upi_id || '';
    const payeeName = cfg?.upi_payee_name || 'Team Fronx';
    const qrUrl     = cfg?.upi_qr_url;
    const note      = `TF Upgrade ${phone.trim()}`;
    const upiLink   = upiId
      ? `upi://pay?pa=${encodeURIComponent(upiId)}`
        + `&pn=${encodeURIComponent(payeeName)}`
        + `&am=${amount}&cu=INR`
        + `&tn=${encodeURIComponent(note)}`
      : null;

    return wrap(
      <>
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <img src="/logo.png" alt="" style={{ width: 64, height: 64, marginBottom: 8 }} />
          <h2 style={{ color: '#F7E8BF', fontSize: 20, fontWeight: 900 }}>
            Upgrade to Premium — ₹{amount}
          </h2>
          <p style={{ color: '#B79F6D', fontSize: 11, letterSpacing: 1 }}>
            {member?.name} · {TIER_LABEL[member?.tier] || 'Basic'} → Premium
          </p>
        </div>

        {error && (
          <div style={{
            marginBottom: 16, padding: '10px 14px',
            background: 'rgba(230,57,70,0.12)',
            border: '1px solid #E63946',
            borderRadius: 8, color: '#E63946', fontSize: 13,
          }}>{error}</div>
        )}

        <div style={{
          padding: 14, background: '#0F0F0F',
          border: '1px solid rgba(212, 181, 116, 0.25)',
          borderRadius: 10, marginBottom: 12,
        }}>
          <div style={{ color: '#B79F6D', fontSize: 10, letterSpacing: 1.5, fontWeight: 700, marginBottom: 6 }}>UPI ID</div>
          <div style={{ color: '#D4B574', fontFamily: 'monospace', fontSize: 16, fontWeight: 700, userSelect: 'all' }}>
            {upiId || '— not configured —'}
          </div>
        </div>

        {qrUrl && (
          <div style={{ padding: 14, background: '#fff', borderRadius: 10, marginBottom: 12, textAlign: 'center' }}>
            <img src={qrUrl} alt="QR" style={{ maxWidth: '100%', maxHeight: 220 }} />
            <div style={{ color: '#666', fontSize: 11, marginTop: 6 }}>Scan with any UPI app</div>
          </div>
        )}

        {upiLink && (
          <a href={upiLink} style={{
            display: 'block', padding: '14px',
            background: '#D4B574', color: '#000',
            borderRadius: 10, textAlign: 'center',
            fontSize: 14, fontWeight: 900, letterSpacing: 0.5,
            textDecoration: 'none', marginBottom: 14,
          }}>📱 Pay ₹{amount} via UPI App</a>
        )}

        <div style={{
          padding: 14, marginBottom: 14,
          background: 'rgba(212, 181, 116, 0.06)',
          border: '1px solid rgba(212, 181, 116, 0.22)',
          borderRadius: 10,
        }}>
          <div style={{ color: '#D4B574', fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 8 }}>
            UPLOAD PAYMENT SCREENSHOT
          </div>
          {shotPreview ? (
            <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden' }}>
              <img src={shotPreview} alt="" style={{ width: '100%', maxHeight: 280, objectFit: 'contain', background: '#000', display: 'block' }} />
              <button type="button"
                onClick={() => { setShotFile(null); setShotPreview(''); if (shotInputRef.current) shotInputRef.current.value = ''; }}
                style={{
                  position: 'absolute', top: 8, right: 8,
                  padding: '6px 12px', background: 'rgba(0,0,0,0.75)',
                  border: '1px solid #E63946', borderRadius: 6,
                  color: '#E63946', fontSize: 12, fontWeight: 600,
                  cursor: 'pointer',
                }}>Remove</button>
            </div>
          ) : (
            <button type="button" onClick={() => shotInputRef.current?.click()}
              style={{
                width: '100%', padding: '24px 16px',
                background: '#0F0F0F',
                border: '2px dashed rgba(212, 181, 116, 0.35)',
                borderRadius: 8, color: '#D8C8A4',
                fontSize: 13, cursor: 'pointer',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', gap: 6,
              }}>
              <span style={{ fontSize: 30 }}>🧾</span>
              <span style={{ fontWeight: 700 }}>Click to attach screenshot</span>
              <span style={{ color: '#B79F6D', fontSize: 11 }}>JPG/PNG · max 5 MB</span>
            </button>
          )}
          <input ref={shotInputRef} type="file" accept="image/*"
            style={{ display: 'none' }} onChange={handleShotChange} />
        </div>

        <button onClick={submitScreenshot}
          disabled={!shotFile || uploading}
          style={{
            width: '100%', padding: '14px',
            background: (!shotFile || uploading) ? '#5A4220' : '#D4B574',
            color: '#000', border: 'none', borderRadius: 12,
            fontSize: 15, fontWeight: 900, letterSpacing: 1,
            cursor: (!shotFile || uploading) ? 'wait' : 'pointer',
          }}>
          {uploading ? 'Uploading…' : 'SUBMIT UPGRADE'}
        </button>
      </>
    );
  }

  // LOOKUP
  const price = member ? upgradePrice(member.tier || 'basic') : 0;

  return wrap(
    <>
      <div style={{ textAlign: 'center', marginBottom: 18 }}>
        <img src="/logo.png" alt="" style={{ width: 60, height: 60, marginBottom: 6 }} />
        <h1 style={{ color: '#F7E8BF', fontSize: 22, fontWeight: 900, marginBottom: 4 }}>
          Upgrade to Premium
        </h1>
        <p style={{ color: '#B79F6D', fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase' }}>
          Unlock mobile app access · 1 year
        </p>
      </div>

      {error && (
        <div style={{
          marginBottom: 16, padding: '10px 14px',
          background: 'rgba(230,57,70,0.12)',
          border: '1px solid #E63946',
          borderRadius: 8, color: '#E63946', fontSize: 13,
        }}>{error}</div>
      )}

      {!member ? (
        <form onSubmit={handleLookup}>
          <label style={{ color: '#D8C8A4', fontSize: 12, fontWeight: 600 }}>
            Your registered phone number
          </label>
          <div style={{ marginTop: 6 }}>
            <PhoneInput value={phone} onChange={setPhone} />
          </div>
          <button type="submit" disabled={busy}
            style={{
              width: '100%', padding: '14px', marginTop: 14,
              background: busy ? '#5A4220' : '#D4B574',
              color: '#000', border: 'none', borderRadius: 12,
              fontSize: 14, fontWeight: 900, letterSpacing: 1,
              cursor: busy ? 'wait' : 'pointer',
            }}>
            {busy ? 'Checking…' : 'FIND MY MEMBERSHIP'}
          </button>
        </form>
      ) : (
        <>
          <div style={{
            padding: 16,
            background: 'rgba(34,197,94,0.08)',
            border: '1px solid rgba(34,197,94,0.3)',
            borderRadius: 10,
            marginBottom: 16,
          }}>
            <div style={{ color: '#22C55E', fontSize: 10, fontWeight: 700, letterSpacing: 1.5, marginBottom: 6 }}>
              MEMBER FOUND
            </div>
            <div style={{ color: '#F7E8BF', fontSize: 16, fontWeight: 800, marginBottom: 4 }}>
              {member.name}
            </div>
            <div style={{ color: '#D8C8A4', fontSize: 13, lineHeight: 1.6 }}>
              ID: {member.member_id || '—'}<br/>
              Current tier: <strong>{TIER_LABEL[member.tier] || 'Basic'}</strong><br/>
              App access today: <strong>{member.mobile_app_access ? 'Yes' : 'No'}</strong>
            </div>
          </div>

          <div style={{
            padding: 14, marginBottom: 16,
            background: 'rgba(212, 181, 116, 0.06)',
            border: '1px solid rgba(212, 181, 116, 0.25)',
            borderRadius: 10, textAlign: 'center',
          }}>
            <div style={{ color: '#B79F6D', fontSize: 11, letterSpacing: 1, marginBottom: 4 }}>
              UPGRADE TO PREMIUM
            </div>
            <div style={{ color: '#F7E8BF', fontSize: 28, fontWeight: 900 }}>
              ₹{price}
            </div>
            <div style={{ color: '#B79F6D', fontSize: 10, marginTop: 4 }}>
              Adds 1-year mobile app access
            </div>
          </div>

          <button onClick={proceedToPayment} disabled={busy}
            style={{
              width: '100%', padding: '14px',
              background: busy ? '#5A4220' : '#D4B574',
              color: '#000', border: 'none', borderRadius: 12,
              fontSize: 14, fontWeight: 900, letterSpacing: 1,
              cursor: busy ? 'wait' : 'pointer',
            }}>
            {busy ? 'Loading…' : 'CONTINUE TO PAYMENT →'}
          </button>

          <button onClick={() => { setMember(null); setPhone('+91'); }}
            style={{
              width: '100%', padding: '10px', marginTop: 8,
              background: 'transparent', color: '#B79F6D',
              border: 'none', fontSize: 12, cursor: 'pointer',
            }}>
            ← Not me, check another number
          </button>
        </>
      )}
    </>
  );
}
